<template>
  <div class="lesson-image-box relative" 
  :style="[CssStyleHelpers.margins(fields.mainGroup?.margins)]">
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <div v-else class="lesson-container w-full h-full m-0  transition-all relative overflow-auto"
         :style="[
          StyleUtils.backgroundWithPattern(
            fields.mainGroup?.color,
            fields.mainGroup?.isShowColor,
            fields.mainGroup?.pattern,
            fields.mainGroup?.isShowPattern
          ),
          CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
        ]">
      <!-- Фонове зображення mainGroup -->
      <div v-if="fields.mainGroup?.isShowMedia"
           class="main-media-background absolute inset-0 z-0">
        <MediaCard
          fill
          :media-type="fields.mainGroup?.type"
          :image-props="fields.mainGroup?.imageProps"
          :video-props="fields.mainGroup?.videoProps"
          :mask-props="fields.mainGroup?.maskProps"
          :object-fit="fields.mainGroup?.objectFit || 'cover'"
          :width="'100%'"
          :aspect-ratio="fields.mainGroup?.imageProps?.aspectRatio || fields.mainGroup?.videoProps?.aspectRatio"
          :max-height="'100%'"
          :border-radius="fields.mainGroup?.borderRadius"
        />
      </div>

      <div v-if="fields.mediaGroup?.isShown" class="w-full h-full flex img-container">
        <div class="media-container">
          <MediaCard
            :media-type="fields.mediaGroup?.type"
            :image-props="fields.mediaGroup?.imageProps"
            :video-props="fields.mediaGroup?.videoProps"
            :mask-props="fields.mediaGroup?.maskProps"
            :object-fit="fields.mediaGroup?.objectFit"
            :width="'100%'"
            :aspect-ratio="fields.mediaGroup?.imageProps?.aspectRatio || fields.mediaGroup?.videoProps?.aspectRatio"
            :max-height="'500px'"
            :border-radius="fields.mainGroup?.borderRadius"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import {useDataFields} from "../composables/data";
import MediaCard from "../cards/MediaCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import {ILayoutComponentData} from "../types/ILayoutFieldData";
import {CssImageHelpers, CssStyleHelpers, CssVideoHelpers, StyleUtils} from "../utils/cssStyleHelpers";

const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

// Используем данные напрямую из newData
const applyDefaultPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData) return {mediaGroup: {}, mainGroup: {}};

  const {getData} = useDataFields(newData);
  const rawMedia = getData('mainGroup.media');

  return {
    mediaGroup: {
      isShown: getData('mainGroup.isShown')?.value ?? true,
      type: rawMedia?.type,
      objectFit: rawMedia?.objectFit ?? 'contain',
      imageProps: CssImageHelpers.imageProps(rawMedia),
      videoProps: CssVideoHelpers.videoProps(rawMedia),
      maskProps: getData('mainGroup.mask'),
      shadow: getData('mainGroup.shadow'),
    },
    mainGroup: {
      isShowColor: getData('mainGroup.isShowColor')?.value,
      color: getData('mainGroup.color')?.value,
      borderRadius: rawMedia?.borderRadius ?? 0,
      isShowMedia: getData('mainGroup.isShowMedia')?.value,
      type: rawMedia?.type,
      objectFit: rawMedia?.objectFit ?? 'contain',
      imageProps: CssImageHelpers.imageProps(rawMedia),
      videoProps: CssVideoHelpers.videoProps(rawMedia),
      maskProps: getData('mainGroup.mediaMask'),
      isShowPattern: getData('mainGroup.isShowPattern')?.value,
      pattern: getData('mainGroup.pattern'),
      margins: getData('mainGroup.margins'),
      paddings: applyDefaultPaddings(getData('mainGroup.paddings')),
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasMedia = fields.value?.mediaGroup?.isShown && (
    fields.value?.mediaGroup?.imageProps?.imageSrc || 
    fields.value?.mediaGroup?.videoProps?.videoSrc
  );
  const hasMainMedia = fields.value?.mainGroup?.isShowMedia && (
    fields.value?.mainGroup?.imageProps?.imageSrc || 
    fields.value?.mainGroup?.videoProps?.videoSrc
  );
  const hasColor = fields.value?.mainGroup?.isShowColor && fields.value?.mainGroup?.color;
  const hasPattern = fields.value?.mainGroup?.isShowPattern;
  
  return !(hasMedia || hasMainMedia || hasColor || hasPattern);
});
</script>
<style scoped>
@reference "tailwindcss";
/* Основные стили для контейнера */
.lesson-image-box {
  @apply w-full flex gap-0;
  min-height: 100%;
  container-type: inline-size;
}

/* Стили для контейнера с патерном */
.lesson-container {
  /* Дозволяємо патерну працювати коректно - важливо! */
  background-attachment: local !important;
  background-origin: border-box !important;
  background-clip: border-box !important;
  /* Забезпечуємо правильне позиціонування патерну */
  background-position: 0% 0% !important;
  background-repeat: repeat !important;
  /* Переконуємося що патерн буде видимий */
  height: 100%;
  display: flex;
  flex-direction: column;
  /* Додаткові стилі для патерну */
  background-color: transparent;
  z-index: 1;
}

/* Переконуємося що патерн відображається поверх інших елементів */
.lesson-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  pointer-events: none;
}

@container (max-width: 640px) {
  .img-container {
    flex-direction: column !important;
  }

  .media-container {
    width: 100% !important;
  }
}

.img-container {
  @apply flex-1;
}

.media-container {
  @apply flex flex-1 justify-center;
  width: 100%;
  height: 100%;
}

/* Стили для фонового зображення mainGroup */
.main-media-background {
  @apply w-full h-full absolute inset-0 z-0;
  overflow: hidden;
}

.main-media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.main-media-background :deep(.media-container-layer),
.main-media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}

.main-media-background :deep(img),
.main-media-background :deep(video) {
  @apply w-full h-full object-cover;
}

</style>
<style>
@import "../styles/dialogs-styles.css";
</style>

